import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { capitalize } from 'lodash'
import { NodevarList } from 'v2/pages/flow/components/nodevar-list'
import type { RProviders } from 'v2/pages/flow/sidebar/types'
import { imageAnalysisConfigSchema, invalidSelectValueSchema } from '../../config/validation.config'
import type { AiSkillsNodeData } from '../../types'
import { SkillInstructionsTextArea } from '../../utils/skill-instructions-textarea'

type FormValues = AiSkillsNodeData['config']

export const getImageAnalysisFields = (
  nodeVars: string[],
  providers?: RProviders,
): GenericFieldConfig<FormValues> => [
  {
    name: 'image_analysis_config.model_id',
    componentType: 'selectWithGroups',
    onChangeListenTo: ['tool_type'],
    zodValidation: invalidSelectValueSchema(
      imageAnalysisConfigSchema.shape.model_id,
      providers?.image_analysis_providers?.flatMap((provider) =>
        provider.models.map((model) => model.id),
      ) ?? [],
    ),
    eventDataExtractor: (value) => value,
    afterOnChange: (value, _, form) => {
      if (!form) return
      const provider = providers?.image_analysis_providers.find((p) =>
        p.models.some((model) => model.id === value.image_analysis_config?.model_id),
      )
      form.setFieldValue('image_analysis_config.provider_id', provider?.id ?? '')
      form.validateAllFields('change')
    },
    inputProps: {
      placeholder: 'Select model provider',
      label: 'Model Provider',
      groups: providers?.image_analysis_providers?.map((provider) => ({
        label: provider.label,
        options: provider.models.map((model) => ({
          label: (
            <div className="flex items-center gap-2">
              <img src={provider.meta.icon_url} alt={provider.label} className="w-4 h-4" />
              {capitalize(`${model.label}`)}
            </div>
          ),
          searchValue: `${provider.label} ${model.label}`,
          value: model.id,
        })),
      })),
    },
    visible: (state) => state.tool_type === 'image_analysis',
  },
  {
    name: 'image_analysis_config.img_url',
    componentType: 'input',
    onChangeListenTo: ['tool_type'],
    zodValidation: imageAnalysisConfigSchema.shape.img_url,
    inputProps: {
      inputMode: 'url',
      placeholder: 'Enter image URL',
      label: 'Image URL',
      endIconClassName: 'pr-2',
      endIcon: (
        <NodevarList nodevars={nodeVars} inputElementSelector="#image_analysis_config.img_url" />
      ),
    },
    visible: (state) => state.tool_type === 'image_analysis',
  },
  {
    name: 'image_analysis_config.instructions',
    label: 'Instructions',
    type: 'custom',
    onChangeListenTo: ['tool_type'],
    component: SkillInstructionsTextArea,
    eventDataExtractor: (e) => e,
    zodValidation: imageAnalysisConfigSchema.shape.instructions,
    inputProps: {
      placeholder:
        'ex: Check for packaging damage in the image., note if any labels or important product details are clearly visible.',
      rows: 4,
      endIcon: (
        <NodevarList
          nodevars={nodeVars}
          inputElementSelector="#image_analysis_config.instructions .ql-ref"
        />
      ),
      'data-testid': 'textarea-skill-instructions',
    },
    visible: (state) => state.tool_type === 'image_analysis',
  },
]
